import { getContext, getElement, store } from '@wordpress/interactivity';

const DEFAULT_GAP = 8;
const EDGE_PADDING = 10;
const HOVER_DELAY = 150;

export function registerPopoverStore(namespace, options = {}) {
  const canInteract = (context) => context.isEnabled !== false;

  store(namespace, {
    state: {
      get isOpen() {
        return getContext().isOpen;
      },

      get isEnabled() {
        return canInteract(getContext());
      },
    },

    actions: {
      toggle(event) {
        const context = getContext();
        if (!canInteract(context)) {
          event?.preventDefault();
          return;
        }

        if (context.isOpen) {
          closePopover(context);
        } else {
          openPopover(context);
        }
      },

      open() {
        const context = getContext();
        if (canInteract(context)) {
          openPopover(context);
        }
      },

      close() {
        closePopover(getContext());
      },

      onClickOutside(event) {
        const context = getContext();
        if (context.isOpen && !context.rootRef?.contains(event.target)) {
          closePopover(context);
        }
      },

      onKeydown(event) {
        if (event.key === 'Escape') {
          closePopover(getContext());
        }
      },

      onTriggerEnter() {
        const context = getContext();
        if (context.triggerType !== 'hover' || !canInteract(context)) {
          return;
        }

        clearTimeout(context.closeTimeoutId);
        openPopover(context);
      },

      onTriggerLeave() {
        const context = getContext();
        if (context.triggerType !== 'hover') {
          return;
        }

        clearTimeout(context.closeTimeoutId);
        context.closeTimeoutId = setTimeout(() => {
          closePopover(context);
        }, HOVER_DELAY);
      },

      onContentEnter() {
        clearTimeout(getContext().closeTimeoutId);
      },

      onContentLeave() {
        const context = getContext();
        if (context.triggerType === 'hover') {
          clearTimeout(context.closeTimeoutId);
          context.closeTimeoutId = setTimeout(() => {
            closePopover(context);
          }, HOVER_DELAY);
        }
      },

      ...options.actions,
    },

    callbacks: {
      initRoot() {
        getContext().rootRef = getElement().ref;
      },

      initTrigger() {
        const context = getContext();
        context.triggerRef = getElement().ref;
        const trigger = getTriggerControl(context);
        const contentId = getContentId(context);

        for (const element of [context.triggerRef, trigger]) {
          if (!element) {
            continue;
          }

          element.setAttribute('aria-controls', contentId);
          element.setAttribute('aria-expanded', 'false');
          element.setAttribute('aria-haspopup', 'dialog');
        }

        syncTriggerAvailability(context, canInteract(context));
      },

      initContent() {
        const context = getContext();
        context.contentRef = getElement().ref;
        context.contentRef.id = getContentId(context);

        if (context.disableWhenContentEmpty) {
          context.isEnabled = context.contentRef.childElementCount > 0;
          syncTriggerAvailability(context, canInteract(context));
        }
      },

      position() {
        positionPopover(getContext());
      },

      ...options.callbacks,
    },
  });
}

function openPopover(context) {
  clearTimeout(context.closeTimeoutId);
  context.isOpen = true;
  updateTriggerAria(context, true);

  window.requestAnimationFrame(() => {
    if (!context.isOpen || !context.triggerRef || !context.contentRef) {
      return;
    }

    positionPopover(context);
    context.contentRef.focus({ preventScroll: true });
  });
}

function closePopover(context) {
  clearTimeout(context.closeTimeoutId);
  context.closeTimeoutId = null;
  if (!context.isOpen) {
    return;
  }

  context.isOpen = false;
  updateTriggerAria(context, false);
  getTriggerControl(context)?.focus({ preventScroll: true });
}

function positionPopover(context) {
  if (!context.isOpen || !context.triggerRef || !context.contentRef) {
    return;
  }

  const triggerRect = context.triggerRef.getBoundingClientRect();
  const contentRect = context.contentRef.getBoundingClientRect();
  const gap = getPopoverGap(context);
  const { placement } = context;
  const contentWidth = contentRect.width;
  const contentHeight = contentRect.height;

  let top;
  let left;

  if (placement === 'top') {
    top = triggerRect.top - contentHeight - gap;
    left = triggerRect.left + triggerRect.width / 2 - contentWidth / 2;
  } else if (placement === 'left') {
    top = triggerRect.top + triggerRect.height / 2 - contentHeight / 2;
    left = triggerRect.left - contentWidth - gap;
  } else if (placement === 'right') {
    top = triggerRect.top + triggerRect.height / 2 - contentHeight / 2;
    left = triggerRect.right + gap;
  } else {
    top = triggerRect.bottom + gap;
    left = triggerRect.left + triggerRect.width / 2 - contentWidth / 2;
  }

  const maxTop = Math.max(
    EDGE_PADDING,
    window.innerHeight - contentHeight - EDGE_PADDING
  );
  const maxLeft = Math.max(
    EDGE_PADDING,
    window.innerWidth - contentWidth - EDGE_PADDING
  );
  context.top = `${Math.min(Math.max(EDGE_PADDING, top), maxTop)}px`;
  context.left = `${Math.min(Math.max(EDGE_PADDING, left), maxLeft)}px`;
}

function getPopoverGap(context) {
  if (!context.rootRef) {
    return DEFAULT_GAP;
  }

  const gap = Number.parseFloat(
    window.getComputedStyle(context.rootRef).rowGap
  );
  return Number.isFinite(gap) ? gap : DEFAULT_GAP;
}

function getTriggerControl(context) {
  const trigger = context.triggerRef;
  if (!trigger) {
    return null;
  }

  const selector =
    'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
  return trigger.matches(selector) ? trigger : trigger.querySelector(selector);
}

function updateTriggerAria(context, isOpen) {
  const expanded = String(isOpen);
  for (const element of [context.triggerRef, getTriggerControl(context)]) {
    if (element) {
      element.setAttribute('aria-expanded', expanded);
    }
  }
}

function syncTriggerAvailability(context, isEnabled) {
  const trigger = context.triggerRef;
  if (!trigger) {
    return;
  }

  const interactiveSelector =
    'a[href], button, input, select, textarea, [tabindex]';
  const interactiveElements = [
    trigger,
    ...trigger.querySelectorAll(interactiveSelector),
  ];

  for (const element of [trigger, getTriggerControl(context)]) {
    if (!element) {
      continue;
    }

    if (isEnabled) {
      if (element.hasAttribute('data-wp-popover-disabled')) {
        element.removeAttribute('aria-disabled');
        element.removeAttribute('data-wp-popover-disabled');
      }
      continue;
    }

    element.setAttribute('aria-disabled', 'true');
    element.setAttribute('data-wp-popover-disabled', 'true');
  }

  for (const element of interactiveElements) {
    if (isEnabled) {
      const originalTabindex = element.getAttribute(
        'data-wp-popover-original-tabindex'
      );
      if (originalTabindex === null) {
        continue;
      }

      if (originalTabindex === '') {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', originalTabindex);
      }
      element.removeAttribute('data-wp-popover-original-tabindex');
      continue;
    }

    if (!element.hasAttribute('data-wp-popover-original-tabindex')) {
      element.setAttribute(
        'data-wp-popover-original-tabindex',
        element.getAttribute('tabindex') ?? ''
      );
    }
    element.setAttribute('tabindex', '-1');
  }
}

function getContentId(context) {
  if (!context.contentId) {
    context.contentId = `popover-content-${Math.random().toString(36).slice(2, 9)}`;
  }

  return context.contentId;
}
